import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AccountCircle, AutoAwesome, People, Search } from '@mui/icons-material';
import Generator from './pages/Generator';
import Encyclopedia from './pages/Encyclopedia';
import Home from './pages/Home';
import Chat from './pages/Chat';
import MyBuilds from './pages/MyBuilds';
import ShareOpen from './pages/ShareOpen';
import Community from './pages/Community';
import CommunityCreatorsDirectory from './pages/CommunityCreatorsDirectory';
import CommunityCreatorBuilds from './pages/CommunityCreatorBuilds';
import Profile from './pages/Profile';
import AiClaim from './pages/AiClaim';
import NotFound from './pages/NotFound';
import Terms from './pages/Terms';
import LanguageDropdown from './components/LanguageDropdown';
import MyBuildsNavMenu from './components/layout/MyBuildsNavMenu';
import SiteNotice from './components/layout/SiteNotice';
import Footer from './pages/Footer';
import { usePageSeo } from './hooks/usePageSeo';
import { NotificationProvider, useNotification } from './components/NotificationSystem';
import { useStaleReload } from './hooks/useStaleReload';
import { OverlayProvider } from './contexts/OverlayContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GeneratorStateProvider } from './contexts/GeneratorStateContext';
import { useGeneratorContext } from './contexts/GeneratorStateContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Overlay from './components/Overlay';
import ScrollToTop from './components/ScrollToTop';
import HeroAnim from './components/hero/HeroAnim';
import { GEN_ROOT_SEGMENT, ROUTE_LANGS, stripLangPrefix } from './utils/buildRoutes';
import { calculateBlendedColor } from './components/utils/ElementalColor';
import { getCharacterClass } from './core/characterUtils';
import { installGlobalErrorReporting, recordCapGateEngaged, recordPageView, recordVisit } from './services/metrics';
import './App.css';

const TXT_CONNECTING = { fr: 'Connexion...', en: 'Connecting...', de: 'Verbindung...', es: 'Conectando...', pt: 'Conectando...' };
const TXT_PRIMARY_NAV = { fr: 'Navigation principale', en: 'Primary', de: 'Hauptnavigation', es: 'Navegación principal', pt: 'Navegação principal' };

const DEFAULT_ROUTE_LANG = 'fr';
const THEME_LIGHT = 'light';
const THEME_DARK = 'dark';
const THEME_STORAGE_KEY = 'theme';
// Derived from the commit count at image build time (deploy.sh passes
// APP_VERSION; see frontend/Dockerfile). Not stored in a file on purpose: a
// hand-maintained counter drifts out of git the moment a deploy edits it and
// nobody commits the edit, which is how prod once served a badge present in no
// commit. Deriving it means the number cannot disagree with the code shipped,
// and a deploy from a stale branch makes it go *down*.
// The fallback is what `npm start` shows; only deploys carry a real number.
const VERSION = process.env.REACT_APP_VERSION || 'dev';
const SHOW_HOME_HERO_ANIM = false;
const SHOW_NAV_BUILD_ELEMENT_DOT = false;

/** Per-route radial page background glow (see .App.app-bg-glow in App.css). */
const PAGE_BG_GLOW = {
  home: true,
  chat: false,
  generator: false,
  encyclopedia: false,
  myBuilds: false,
  community: false,
  communityCreators: false,
  communityCreator: false,
  profile: false,
  share: false,
  aiClaim: false,
  terms: false,
  notFound: false,
};

/**
 * Where the glow sits, per route (only matters where PAGE_BG_GLOW is true):
 * - 'fixed': lives on .App, which never scrolls — glued to the viewport
 *   regardless of how far the page scrolls. The home hero look.
 * - 'absolute': lives on .main-content-body instead, anchored to the
 *   bottom of that page's own content so it scrolls away with the page
 *   rather than lingering in the viewport.
 * Unlisted routes default to 'absolute'.
 */
const PAGE_BG_GLOW_POSITION = {
  home: 'fixed',
};

/**
 * Per-route footer. It is app chrome, rendered as a child of the scroll
 * container so its top rule can span the viewport like the navbar's — which is
 * why it lives here rather than inside PageTemplate. Routes that own the whole
 * viewport opt out: the chat pins its own input to the bottom, and the
 * redirect landings are gone before a footer would be worth reading.
 */
const PAGE_FOOTER = {
  home: true,
  chat: false,
  generator: true,
  encyclopedia: true,
  myBuilds: true,
  community: true,
  communityCreators: true,
  communityCreator: true,
  profile: true,
  share: false,
  aiClaim: false,
  terms: true,
  notFound: false,
};

function getPageKey(pathname, lang) {
  const base = `/${lang}`;
  const rest = pathname === base || pathname === `${base}/`
    ? ''
    : pathname.slice(base.length).replace(/^\//, '');

  if (!rest) return 'home';
  if (rest.startsWith('chat/')) return 'chat';
  if (rest === GEN_ROOT_SEGMENT || rest.startsWith(`${GEN_ROOT_SEGMENT}/`)) return 'generator';
  if (rest.startsWith('s/')) return 'share';
  if (rest === 'encyclopedia') return 'encyclopedia';
  if (rest === 'my-builds') return 'myBuilds';
  if (rest === 'community/creators') return 'communityCreators';
  if (rest.startsWith('community/')) return 'communityCreator';
  if (rest === 'community') return 'community';
  if (rest === 'profile') return 'profile';
  if (rest === 'ai/claim') return 'aiClaim';
  if (rest === 'terms') return 'terms';
  return 'notFound';
}

const NAV_TRANSLATIONS = {
  generator: { fr: 'Générateur', en: 'Generator', de: 'Generator', es: 'Generador', pt: 'Gerador' },
  encyclopedia: { fr: 'Encyclopédie', en: 'Encyclopedia', de: 'Enzyklopädie', es: 'Enciclopedia', pt: 'Enciclopédia' },
  myBuilds: { fr: 'Mes stuffs', en: 'My builds', de: 'Meine Builds', es: 'Mis builds', pt: 'Minhas builds' },
  community: { fr: 'Communauté', en: 'Community', de: 'Community', es: 'Comunidad', pt: 'Comunidade' },
  connect: { fr: 'SE CONNECTER', en: 'LOG IN', de: 'ANMELDEN', es: 'INICIAR SESIÓN', pt: 'ENTRAR' },
  profile: { fr: 'Profil', en: 'Profile', de: 'Profil', es: 'Perfil', pt: 'Perfil' },
};

const getNavTranslation = (key, lang) => {
  const translation = NAV_TRANSLATIONS[key];
  if (!translation) return '';
  return translation[lang] || translation.en || translation.fr || '';
};

const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_API_URL) {
    return window.__RUNTIME_CONFIG__.REACT_APP_API_URL;
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return 'http://localhost:8000';
};

export const getServerUrl = () => {
  const apiUrl = getApiUrl();
  const serverUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
  const runtimeDebug = typeof window !== 'undefined'
    ? window.__RUNTIME_CONFIG__?.REACT_APP_DEBUG_SERVER_URL_LOG === 'true'
    : false;
  const envDebug = process.env.REACT_APP_DEBUG_SERVER_URL_LOG === 'true';
  const shouldLog = runtimeDebug || envDebug;
  if (shouldLog && !getServerUrl._logged) {
    console.log('[App] Server URL resolved:', {
      source: window.__RUNTIME_CONFIG__ ? 'runtime config' :
        process.env.REACT_APP_API_URL ? 'build-time env' : 'fallback',
      apiUrl,
      serverUrl,
      runtimeConfig: window.__RUNTIME_CONFIG__,
    });
    getServerUrl._logged = true;
  }
  return serverUrl;
};

export const SERVER_URL = getServerUrl;

function pathWithLang(lang, subPath) {
  const path = subPath === '/' || !subPath ? '' : subPath;
  return `/${lang}${path}`;
}

function withLang(Component) {
  return function WithLang() {
    const { lang } = useParams();
    const safeLang = ROUTE_LANGS.includes(lang) ? lang : DEFAULT_ROUTE_LANG;
    return <Component lang={safeLang} />;
  };
}

function LegacyGeneratorRedirect() {
  const { lang, '*': rest } = useParams();
  const safeLang = ROUTE_LANGS.includes(lang) ? lang : DEFAULT_ROUTE_LANG;
  const suffix = rest ? `/${rest}` : '';
  return <Navigate to={`/${safeLang}/${GEN_ROOT_SEGMENT}${suffix}`} replace />;
}

const HomeWithLang = withLang(Home);
function ChatRoute() {
  const { lang: langParam, conversationId } = useParams();
  const safeLang = ROUTE_LANGS.includes(langParam) ? langParam : DEFAULT_ROUTE_LANG;
  // key forces a clean remount when switching between conversations.
  return <Chat key={conversationId} lang={safeLang} conversationId={conversationId} />;
}
const GeneratorWithLang = withLang(Generator);
const EncyclopediaWithLang = withLang(Encyclopedia);
const MyBuildsWithLang = withLang(MyBuilds);
const ShareOpenWithLang = withLang(ShareOpen);
const CommunityWithLang = withLang(Community);
const CommunityCreatorsDirectoryWithLang = withLang(CommunityCreatorsDirectory);
const CommunityCreatorWithLang = withLang(CommunityCreatorBuilds);
const ProfileWithLang = withLang(Profile);
const AiClaimWithLang = withLang(AiClaim);
const TermsWithLang = withLang(Terms);
const NotFoundWithLang = withLang(NotFound);

function AppLayout() {
  const { lang: langParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = ROUTE_LANGS.includes(langParam) ? langParam : DEFAULT_ROUTE_LANG;
  usePageSeo();
  // A capped guest who navigates somewhere else hasn't abandoned the app.
  // No-op unless a cap gate in this session is still awaiting an outcome.
  useEffect(() => {
    recordCapGateEngaged();
  }, [location.pathname]);
  // Visits, not page views: this fires on every route change but only beacons
  // when the visitor has been away long enough to be back (see `recordVisit`).
  // Navigating is what keeps a visit alive, so it belongs here and not on mount.
  // Page views ride the same effect and count each page landed on.
  useEffect(() => {
    recordVisit(location.pathname);
    recordPageView(location.pathname);
  }, [location.pathname]);
  const { authState, isAuthenticated, user } = useAuth();
  const { build, getActiveBuildPath } = useGeneratorContext();
  const generatorNavBadge = useMemo(() => {
    const { r, g, b } = calculateBlendedColor(build.getStats());
    const raw = build.getCharacterClass();
    const cls =
      raw && typeof raw === 'object' && raw.icon
        ? raw
        : getCharacterClass(typeof raw === 'string' ? raw : raw?.name);
    const icon = cls?.icon ?? '';
    const classAlt = (cls?.label && (cls.label[lang] || cls.label.en)) || cls?.name || '';
    return { rgb: `rgb(${r}, ${g}, ${b})`, icon, classAlt };
  }, [build, lang]);
  const [theme] = useState(() => {
    if (typeof window === 'undefined') {
      return THEME_DARK;
    }
    try {
      const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === THEME_LIGHT || stored === THEME_DARK) {
        return stored;
      }
    } catch (error) {
      // Ignore storage access errors.
    }
    return THEME_DARK;
  });
  const mainScrollRef = useRef(null);
  useNotification();
  // A tab left open across a deploy holds chunk URLs that no longer exist.
  // Offer it the new version rather than letting each feature find out alone.
  useStaleReload(lang);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-theme', theme);
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
      } catch (error) {
        // Ignore storage write errors.
      }
    }
  }, [theme]);

  if (!ROUTE_LANGS.includes(langParam)) {
    const rest = stripLangPrefix(location.pathname);
    return <Navigate to={pathWithLang(DEFAULT_ROUTE_LANG, rest ? `/${rest}` : '/')} replace />;
  }

  const handleSetLanguage = (newLang) => {
    const rest = stripLangPrefix(location.pathname);
    navigate(pathWithLang(newLang, rest ? `/${rest}` : '/'));
  };

  const base = `/${lang}`;
  const communityNavRoot = `${base}/community`;
  const isCommunityNavActive =
    location.pathname === communityNavRoot
    || location.pathname === `${communityNavRoot}/`
    || location.pathname.startsWith(`${communityNavRoot}/`);
  const generatorNavRoot = `${base}/${GEN_ROOT_SEGMENT}`;
  const isGeneratorNavActive =
    location.pathname === generatorNavRoot
    || location.pathname === `${generatorNavRoot}/`
    || location.pathname.startsWith(`${generatorNavRoot}/`);
  const generatorNavTarget = getActiveBuildPath(lang) || `${base}/${GEN_ROOT_SEGMENT}`;
  const isHomePage = location.pathname === base || location.pathname === `${base}/`;
  const pageKey = getPageKey(location.pathname, lang);
  const showPageBgGlow = PAGE_BG_GLOW[pageKey] ?? false;
  const bgGlowPosition = PAGE_BG_GLOW_POSITION[pageKey] ?? 'absolute';
  const isBgGlowFixed = showPageBgGlow && bgGlowPosition === 'fixed';
  const isBgGlowAbsolute = showPageBgGlow && bgGlowPosition === 'absolute';
  const showFooter = PAGE_FOOTER[pageKey] ?? false;

  return (
    <ThemeProvider theme={theme}>
      <div className={`App${showPageBgGlow ? ' app-bg-glow' : ''}${isBgGlowFixed ? ' app-bg-glow-fixed' : ''}`}>
        <header className="top-navbar">
          <div className="top-navbar-inner">
            <div className="top-navbar-left">
              <NavLink to={base} end className={({ isActive }) => `top-navbar-brand-link ${isActive ? 'active' : ''}`}>
                <h6 className="top-navbar-brand">dofgen</h6>
              </NavLink>
            </div>
            <nav className="top-navbar-nav" aria-label={TXT_PRIMARY_NAV[lang] || TXT_PRIMARY_NAV.en}>
              <NavLink to={generatorNavTarget} className={() => (isGeneratorNavActive ? 'active' : '')}>
                <AutoAwesome fontSize="small" />
                <div className="nav-generator-label">
                  <h6>{getNavTranslation('generator', lang)}</h6>
                  {SHOW_NAV_BUILD_ELEMENT_DOT && (
                    <span
                      className="nav-build-element-dot"
                      style={{ backgroundColor: generatorNavBadge.rgb }}
                    >
                      {generatorNavBadge.icon ? (
                        <img
                          className="nav-build-class-icon"
                          src={generatorNavBadge.icon}
                          alt={generatorNavBadge.classAlt}
                          draggable={false}
                        />
                      ) : null}
                    </span>
                  )}
                </div>
              </NavLink>
              <MyBuildsNavMenu lang={lang} base={base} />
              <NavLink to={`${base}/encyclopedia`} className={({ isActive }) => (isActive ? 'active' : '')}>
                <Search fontSize="small" />
                <h6>{getNavTranslation('encyclopedia', lang)}</h6>
              </NavLink>
              <NavLink
                to={communityNavRoot}
                className={() => (isCommunityNavActive ? 'active' : '')}
              >
                <People fontSize="small" />
                <h6>{getNavTranslation('community', lang)}</h6>
              </NavLink>
            </nav>
            <div className="top-navbar-right">
              <div className="app-version-badge">{VERSION}</div>
              <LanguageDropdown language={lang} setLanguage={handleSetLanguage} />
              <NavLink
                to={`${base}/profile`}
                className={({ isActive }) => `profile-btn ${isActive ? 'active' : ''} ${isAuthenticated ? 'profile-btn-authenticated' : 'profile-btn-anonymous'
                  }`}
                aria-busy={authState === 'loading' ? 'true' : 'false'}
                onClick={(event) => {
                  if (authState === 'loading') {
                    event.preventDefault();
                  }
                }}
              >
                {isAuthenticated && user?.avatar_url ? (
                  <img className="profile-avatar-icon" src={user.avatar_url} alt="" />
                ) : (
                  <AccountCircle fontSize="small" />
                )}
                <h6 className="profile-name">
                  {authState === 'loading'
                    ? (TXT_CONNECTING[lang] || TXT_CONNECTING.en)
                    : isAuthenticated
                      ? (user?.display_name || user?.discord_username || getNavTranslation('profile', lang))
                      : getNavTranslation('connect', lang)}
                </h6>
              </NavLink>
            </div>
          </div>
        </header>
        <SiteNotice lang={lang} />
        <ScrollToTop scrollRootRef={mainScrollRef} />
        <div className="main-content" ref={mainScrollRef}>
          {isHomePage && SHOW_HOME_HERO_ANIM && <HeroAnim />}
          <div className={`main-content-body${isBgGlowAbsolute ? ' app-bg-glow-absolute' : ''}`}>
            <OverlayProvider>
              <div className="main-content-wrapper">
                <Overlay />
                <div className="main-content-outlet">
                  <Outlet />
                </div>
              </div>
            </OverlayProvider>
            {showFooter && <Footer lang={lang} />}
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}

function App() {
  useEffect(() => {
    installGlobalErrorReporting();
  }, []);
  return (
    <NotificationProvider>
      <AuthProvider>
        <GeneratorStateProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/" element={<Navigate to={pathWithLang(DEFAULT_ROUTE_LANG, '/')} replace />} />
              <Route path="/:lang" element={<AppLayout />}>
                <Route index element={<HomeWithLang />} />
                <Route path="chat/:conversationId" element={<ChatRoute />} />
                <Route path={GEN_ROOT_SEGMENT} element={<GeneratorWithLang />} />
                <Route path={`${GEN_ROOT_SEGMENT}/:dataTab`} element={<GeneratorWithLang />} />
                <Route path={`${GEN_ROOT_SEGMENT}/:userSlug/:buildCode`} element={<GeneratorWithLang />} />
                <Route path={`${GEN_ROOT_SEGMENT}/:userSlug/:buildCode/:dataTab`} element={<GeneratorWithLang />} />
                <Route path="generator/*" element={<LegacyGeneratorRedirect />} />
                <Route path="s/:slug" element={<ShareOpenWithLang />} />
                <Route path="encyclopedia" element={<EncyclopediaWithLang />} />
                <Route path="my-builds" element={<MyBuildsWithLang />} />
                <Route path="community/creators" element={<CommunityCreatorsDirectoryWithLang />} />
                <Route path="community/:publicSlug" element={<CommunityCreatorWithLang />} />
                <Route path="community" element={<CommunityWithLang />} />
                <Route path="profile" element={<ProfileWithLang />} />
                <Route path="ai/claim" element={<AiClaimWithLang />} />
                <Route path="terms" element={<TermsWithLang />} />
                <Route path="*" element={<NotFoundWithLang />} />
              </Route>
            </Routes>
          </Router>
        </GeneratorStateProvider>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
